import { FC } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import {
  Button,
  FormRadio,
  FormTextArea,
  IRadioOption,
  useModal,
  useToast,
} from '../../../../../../components'
import {
  ButtonType,
  DuvidaResolvida,
  formatDateTime,
  ID,
  showError,
} from '../../../../../../../_metronic/helpers'
import { registrarDesfecho } from '../../core/_requests'

type Props = {
  atendimentoId: ID
  prazoAvaliacao?: string
}

type DesfechoForm = {
  duvidaResolvida: DuvidaResolvida | null
  /** `null` = não informado, que é o padrão e também uma opção explícita. */
  intencaoEncaminhar: boolean | null
  encaminhamentoEvitado: boolean | null
  satisfacao: number | null
  acaoAdotada: string
}

const ACAO_ADOTADA_MAX = 255

const DUVIDA_OPCOES: IRadioOption<DuvidaResolvida>[] = [
  { id: 'sim', descricao: 'Sim', value: DuvidaResolvida.Sim },
  { id: 'parcialmente', descricao: 'Parcialmente', value: DuvidaResolvida.Parcialmente },
  { id: 'nao', descricao: 'Não', value: DuvidaResolvida.Nao },
]

const ENCAMINHAMENTO_OPCOES: IRadioOption<boolean>[] = [
  { id: 'sim', descricao: 'Sim', value: true },
  { id: 'nao', descricao: 'Não', value: false },
]

const INTENCAO_OPCOES: IRadioOption<boolean | null>[] = [
  ...ENCAMINHAMENTO_OPCOES,
  { id: 'nao_informado', descricao: 'Não informado', value: null },
]

const SATISFACAO_OPCOES: IRadioOption<number>[] = [
  { id: 1, descricao: '1 · Muito insatisfeito', value: 1 },
  { id: 2, descricao: '2 · Insatisfeito', value: 2 },
  { id: 3, descricao: '3 · Neutro', value: 3 },
  { id: 4, descricao: '4 · Satisfeito', value: 4 },
  { id: 5, descricao: '5 · Muito satisfeito', value: 5 },
]

const desfechoSchema = Yup.object().shape({
  duvidaResolvida: Yup.string().nullable().required('Informe se a dúvida foi resolvida'),
  intencaoEncaminhar: Yup.boolean().nullable(),
  encaminhamentoEvitado: Yup.boolean()
    .nullable()
    .required('Informe se o encaminhamento foi evitado'),
  satisfacao: Yup.number().nullable().min(1).max(5).required('Informe a sua satisfação'),
  acaoAdotada: Yup.string().max(ACAO_ADOTADA_MAX, `Use no máximo ${ACAO_ADOTADA_MAX} caracteres`),
})

/**
 * Desfecho da interconsulta pelo solicitante. Opcional e único: ao registrar, a situação vira
 * AVALIADA e o ciclo encerra (sem nova réplica).
 */
export const DesfechoModal: FC<Props> = ({ atendimentoId, prazoAvaliacao }) => {
  const { closeModal } = useModal()
  const { showToast } = useToast()

  const formik = useFormik<DesfechoForm>({
    initialValues: {
      duvidaResolvida: null,
      intencaoEncaminhar: null,
      encaminhamentoEvitado: null,
      satisfacao: null,
      acaoAdotada: '',
    },
    validationSchema: desfechoSchema,
    onSubmit: async (values) => {
      try {
        await registrarDesfecho(atendimentoId, {
          duvidaResolvida: values.duvidaResolvida as DuvidaResolvida,
          intencaoEncaminhar: values.intencaoEncaminhar ?? undefined,
          encaminhamentoEvitado: values.encaminhamentoEvitado as boolean,
          satisfacao: values.satisfacao as number,
          acaoAdotada: values.acaoAdotada.trim() || undefined,
        })
        showToast({ variant: 'success', title: 'Desfecho registrado' })
        closeModal()
      } catch (error) {
        showError(error)
      }
    },
  })

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <p className='text-gray-800 mb-6'>
        Conte como o parecer ajudou no cuidado do paciente. Depois de registrado, o desfecho encerra
        a interconsulta e não é mais possível replicar.
        {prazoAvaliacao && (
          <>
            {' '}
            Prazo: <strong>{formatDateTime(prazoAvaliacao)}</strong>.
          </>
        )}
      </p>
      <div className='mb-6' role='radiogroup' aria-label='A dúvida clínica foi resolvida?'>
        <FormRadio
          formik={formik}
          name='duvidaResolvida'
          label='A dúvida clínica foi resolvida?'
          options={DUVIDA_OPCOES}
          isRequired
        />
      </div>
      <div
        className='mb-6'
        role='radiogroup'
        aria-label='Antes do parecer, você tinha intenção de encaminhar o paciente à atenção especializada?'
      >
        <FormRadio
          formik={formik}
          name='intencaoEncaminhar'
          label='Antes do parecer, você tinha intenção de encaminhar o paciente à atenção especializada?'
          options={INTENCAO_OPCOES}
        />
      </div>
      <div
        className='mb-6'
        role='radiogroup'
        aria-label='A interconsulta evitou um encaminhamento presencial?'
      >
        <FormRadio
          formik={formik}
          name='encaminhamentoEvitado'
          label='A interconsulta evitou um encaminhamento presencial?'
          options={ENCAMINHAMENTO_OPCOES}
          isRequired
        />
      </div>
      <div className='mb-6' role='radiogroup' aria-label='Qual a sua satisfação com o parecer?'>
        <FormRadio
          formik={formik}
          name='satisfacao'
          label='Qual a sua satisfação com o parecer?'
          options={SATISFACAO_OPCOES}
          isRequired
        />
      </div>
      <FormTextArea
        formik={formik}
        name='acaoAdotada'
        label={`Ação adotada (opcional, até ${ACAO_ADOTADA_MAX} caracteres)`}
        placeholder='Ex.: ajustei a medicação conforme o parecer'
        rows={3}
      />
      <div className='d-flex justify-content-end gap-3 pt-6'>
        <Button className='btn-secondary' onClick={closeModal} disabled={formik.isSubmitting}>
          Cancelar
        </Button>
        <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
          Registrar desfecho
        </Button>
      </div>
    </form>
  )
}
